import React, { useEffect, useMemo, useState } from 'react';
import { Gear } from '@gravity-ui/icons';
import { useUrlState } from '../../../hooks/useUrlState';
import { listFactories } from '../../../services/factories';
import { isAdmin } from '../../../services/api';
import { bulkOperationalIssueAction } from '../../../services/operationalIssues';
import { describeHttpError } from '../../../utils/apiError';
import { useOperationalIssues, ISSUES_URL_SCHEMA, ISSUES_PAGE_SIZE } from './useOperationalIssues';
import { useFactoryCameras } from './useFactoryCameras';
import {
  STATUS_TABS, ARCHIVE_VIEWS, buildPeriodLabel, countTabTotal,
  bulkActionsForView, describeBulkActionResult,
} from './issueConstants';
import IssueCard from './IssueCard';
import IssueDetailDrawer from './IssueDetailDrawer';
import IssueSettingsModal from './IssueSettingsModal';
import TimezoneNotice from '../components/TimezoneNotice';
import SummaryStateBlock from '../components/SummaryStateBlock';
import './OperationalIssuesTab.css';

/*
 * «Системные проблемы» (ТЗ №2, OPS-ISSUES03).
 * Спека: docs/superpowers/specs/2026-09-21-summary-ops-issues-timezone-design.md §3
 *
 * Список — только backend list API; период — list.window_days (applied);
 * порядок items — backend; lifecycle/severity/trend — как пришли.
 * Date preset Executive Summary сюда не проникает.
 *
 * ТЗ v4 §30 (FE-FIX27): archive_state — вид списка, а не фильтр lifecycle.
 * Действия применяются одним bulk-запросом с явным подтверждением; частичного
 * оптимистичного успеха нет — после ответа (и после ошибки) список
 * перечитывается, потому что состояние знает backend.
 */

const normalizeFactories = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const OperationalIssuesTab = () => {
  const [url, setUrl] = useUrlState(ISSUES_URL_SCHEMA);
  const { data, status, error, reload } = useOperationalIssues(url);
  const [factories, setFactories] = useState([]);
  const cameras = useFactoryCameras(url.factoryId);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkError, setBulkError] = useState('');
  const admin = isAdmin();

  useEffect(() => {
    let cancelled = false;
    listFactories()
      .then((list) => { if (!cancelled) setFactories(normalizeFactories(list)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const windowDays = data?.window_days;
  const counts = data?.status_counts || {};
  const archiveCounts = data?.archive_state_counts || {};
  const tabCount = (id) => (id === '' ? countTabTotal(counts) : (counts[id] ?? 0));
  const archiveView = url.archiveState || 'active';
  const actions = bulkActionsForView(archiveView);

  // Смена фильтра — offset=0 и отдельный шаг истории (ТЗ №2 §7).
  const setFilter = (partial) => setUrl({ ...partial, offset: 0 }, { replace: false });
  const openIssue = (issueId) => setUrl({ issueId }, { replace: false });
  const closeIssue = () => setUrl({ issueId: '' }, { replace: false });

  const clearSelection = () => { setSelectedIds([]); setBulkError(''); };

  const setView = (view) => {
    clearSelection();
    setBulkMessage('');
    setFilter({ archiveState: view });
  };

  const toggleSelect = (issueId) => {
    setBulkError('');
    setSelectedIds((prev) => (
      prev.includes(issueId) ? prev.filter((id) => id !== issueId) : [...prev, issueId]
    ));
  };

  const items = useMemo(() => data?.items || [], [data]);
  const pageIds = useMemo(() => items.map((issue) => issue.issue_id), [items]);
  const selectedOnPage = selectedIds.filter((id) => pageIds.includes(id));
  const allSelected = pageIds.length > 0 && selectedOnPage.length === pageIds.length;

  const toggleSelectAll = () => {
    setBulkError('');
    setSelectedIds(allSelected ? [] : pageIds);
  };

  const runBulk = async (action) => {
    if (selectedOnPage.length === 0) return;
    if (!window.confirm(action.confirm(selectedOnPage.length))) return;

    setBulkBusy(true);
    setBulkError('');
    setBulkMessage('');
    try {
      const result = await bulkOperationalIssueAction({
        issue_ids: selectedOnPage,
        action: action.id,
        confirm: true,
      });
      setBulkMessage(`${action.label}: ${describeBulkActionResult(result)}`);
      setSelectedIds([]);
    } catch (requestError) {
      setBulkError(describeHttpError(requestError, 'Не удалось выполнить действие'));
    } finally {
      setBulkBusy(false);
      reload();
    }
  };

  const total = data?.total ?? 0;
  const canPrev = url.offset > 0;
  const canNext = url.offset + ISSUES_PAGE_SIZE < total;

  const renderBulkBar = () => (
    <div className="oi-bulk" role="group" aria-label="Массовые действия">
      <label className="oi-bulk-all">
        <input
          type="checkbox"
          checked={allSelected}
          disabled={pageIds.length === 0 || bulkBusy}
          onChange={toggleSelectAll}
        />
        <span>Выбрать все на странице</span>
      </label>
      <span className="oi-bulk-count">Выбрано {selectedOnPage.length}</span>
      {actions.map((action) => (
        <button
          key={action.id}
          type="button"
          className={`sum-btn${action.destructive ? ' oi-bulk-btn--danger' : ''}`}
          disabled={selectedOnPage.length === 0 || bulkBusy}
          onClick={() => runBulk(action)}
        >
          {action.label}
        </button>
      ))}
      {selectedOnPage.length > 0 && (
        <button type="button" className="sum-btn" disabled={bulkBusy} onClick={clearSelection}>
          Снять выделение
        </button>
      )}
      {bulkMessage && <span className="oi-bulk-msg">{bulkMessage}</span>}
      {bulkError && <span className="oi-bulk-msg oi-bulk-msg--error" role="alert">{bulkError}</span>}
    </div>
  );

  const renderBody = () => {
    if (!data) {
      if (status === 'error') return <SummaryStateBlock state="error" message={error.message} onRetry={reload} />;
      return <SummaryStateBlock state="loading" />;
    }
    return (
      <>
        {status === 'error' && <SummaryStateBlock state="error" message={error.message} onRetry={reload} />}
        {renderBulkBar()}
        <div className={`oi-list${status === 'loading' ? ' oi-list--stale' : ''}`} aria-busy={status === 'loading'}>
          {items.length === 0
            ? (
              <SummaryStateBlock
                state="empty"
                message={archiveView === 'archived'
                  ? 'В архиве пусто'
                  : 'Системных проблем в доступном scope нет'}
              />
            )
            : items.map((issue) => (
              <IssueCard
                key={issue.issue_id}
                issue={issue}
                windowDays={windowDays}
                onOpen={openIssue}
                selected={selectedIds.includes(issue.issue_id)}
                onToggleSelect={toggleSelect}
              />
            ))}
        </div>
        {total > ISSUES_PAGE_SIZE && (
          <div className="oi-pagination">
            <span>Показано {items.length ? url.offset + 1 : 0}–{url.offset + items.length} из {total}</span>
            <button
              type="button"
              className="sum-btn"
              disabled={!canPrev}
              onClick={() => setUrl({ offset: Math.max(0, url.offset - ISSUES_PAGE_SIZE) }, { replace: false })}
            >
              Назад
            </button>
            <button
              type="button"
              className="sum-btn"
              disabled={!canNext}
              onClick={() => setUrl({ offset: url.offset + ISSUES_PAGE_SIZE }, { replace: false })}
            >
              Вперёд
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="oi-container">
      <div className="oi-header">
        <div className="oi-header-line">
          {windowDays != null && <span className="oi-period">{buildPeriodLabel(windowDays)}</span>}
          {data && <TimezoneNotice scope={data} />}
        </div>
        {admin && (
          <button type="button" className="sum-btn oi-settings-btn" onClick={() => setSettingsOpen(true)}>
            <Gear width={16} height={16} /> Настройки
          </button>
        )}
      </div>

      <div className="oi-views" role="group" aria-label="Архив">
        {ARCHIVE_VIEWS.map((view) => (
          <button
            key={view.id}
            type="button"
            className={`sum-preset${archiveView === view.id ? ' sum-preset--active' : ''}`}
            onClick={() => setView(view.id)}
          >
            {view.label}
            {archiveCounts[view.id] != null && <span className="oi-tab-count">{archiveCounts[view.id]}</span>}
          </button>
        ))}
      </div>

      <div className="oi-filters">
        <label className="sum-field">
          <span className="sum-field-label">Объект</span>
          <select
            className="sum-select"
            value={url.factoryId}
            onChange={(event) => setFilter({ factoryId: event.target.value, cameraId: '' })}
          >
            <option value="">Все объекты</option>
            {factories.map((factory) => (
              <option key={factory.id} value={String(factory.id)}>{factory.name || `#${factory.id}`}</option>
            ))}
          </select>
        </label>
        <label className="sum-field">
          <span className="sum-field-label">Камера</span>
          <select
            className="sum-select"
            value={url.cameraId}
            disabled={!url.factoryId}
            onChange={(event) => setFilter({ cameraId: event.target.value })}
          >
            <option value="">Все камеры</option>
            {cameras.map((camera) => (
              <option key={camera.id} value={String(camera.id)}>{camera.name || `Камера #${camera.id}`}</option>
            ))}
          </select>
        </label>
        <div className="oi-tabs" role="group" aria-label="Статус">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`sum-preset${url.status === tab.id ? ' sum-preset--active' : ''}`}
              onClick={() => setFilter({ status: tab.id })}
            >
              {tab.label} <span className="oi-tab-count">{tabCount(tab.id)}</span>
            </button>
          ))}
        </div>
      </div>

      {renderBody()}

      {url.issueId && <IssueDetailDrawer issueId={url.issueId} onClose={closeIssue} />}
      {settingsOpen && <IssueSettingsModal onClose={() => setSettingsOpen(false)} onApplied={reload} />}
    </div>
  );
};

export default OperationalIssuesTab;
